import type { Chat, Message } from '@jerome2/shared';
import { createStore } from '../lib/store.ts';
import { app } from './app.ts';
import { chatStore, stopRequests } from './chat.ts';
import { tabs, UNREAD_KEPT, type PaneId, type TabsState } from './tabs.ts';

// Werker per vraag en ongelezen antwoord (ticket 30961, deel 2; wens van de eigenaar: "bij elke vraag een werker rechts tot het
// antwoord er is, en een ongelezen-markering tot de tab weer geopend wordt"). De bron is de status van de gesprekken in de chatstore
// (chat.status, en wat de client zelf zet bij versturen en Stop): de server verandert niet en er komt geen taak bij.
// - Zolang een gewoon gesprek (soort chat, ook dat van een tabblad van een mail) bezig is, staat het als rij in de werkerskolom
//   (shell/WorkersRail.tsx) met hoe lang het al loopt; `turnStarts` onthoudt wanneer het bezig ging. Was het al bezig toen de client
//   het voor het eerst zag (herladen tijdens een beurt), dan telt de laatste wijziging op de server, want die zet het gesprek op bezig.
// - Gaat zo'n gesprek van bezig naar rust of fout en schreef Jerome in die beurt een antwoord, terwijl het gesprek niet in beeld was,
//   dan komt het in `unread` van de tabstaat: een stip op zijn tabblad en bij zijn rij in de gesprekkenlijst. Een antwoord is tekst van
//   Jerome, of een fout als laatste bericht van de beurt; een stap of een notitie niet. Stop is geen antwoord: de server schrijft dan
//   een fout ("afgebroken") met daarna een statusregel, en Stop op deze client (`stopRequests`) geeft nooit een stip. Kwam de rust
//   binnen vóór het antwoord (na een onderbroken verbinding meldt de lijst van gesprekken de rust, en komen de berichten daarna), dan
//   telt een antwoord dat binnen twee minuten met de berichten komt nog mee (`LATE_MS`).
//   In beeld is het gesprek van het actieve chattabblad van een zichtbaar paneel (`visibleChats`) terwijl het venster zichtbaar is
//   (`document.visibilityState`).
// - De stip gaat weg zodra het gesprek in beeld is terwijl het venster zichtbaar is: bij het activeren of openen van zijn tabblad, en
//   bij terugkeer naar het venster als zijn tabblad al actief was (dan staat het antwoord al voor de eigenaar).

/** Per gesprek dat bezig is: wanneer de beurt begon (ms, de klok van de client), voor de tijd in de werkerskolom. */
export const turnStarts = createStore<Record<string, number>>({});

const isTurnChat = (c: Chat): boolean => c.kind === 'chat' && c.status === 'running';

/** Wanneer de beurt van een gesprek begon: zoals vastgelegd, anders de laatste wijziging op de server (niet later dan nu). */
export function turnStart(chat: Chat, starts: Record<string, number>, now = Date.now()): number {
  return starts[chat.id] ?? Math.min(now, Date.parse(chat.updatedAt) || now);
}

/** De gewone gesprekken waar Jerome mee bezig is: de rijen van de werkerskolom, de langst lopende eerst. */
export function runningChats(chats: Record<string, Chat>, starts: Record<string, number>): Chat[] {
  const now = Date.now();
  return Object.values(chats)
    .filter(isTurnChat)
    .sort((a, b) => turnStart(a, starts, now) - turnStart(b, starts, now));
}

/** Hoeveel gewone gesprekken bezig zijn: de kop van de werkerskolom, de strook en de bliksem op de telefoon tellen ze mee. */
export function runningChatCount(chats: Record<string, Chat>): number {
  return Object.values(chats).filter(isTurnChat).length;
}

/** De panelen in beeld: op de telefoon het actieve paneel (of links als dat leeg is, zoals de schil), op een breed scherm met twee
 * panelen beide, anders links. */
export function visiblePanes(s: TabsState, view: { narrow: boolean; wide: boolean }): PaneId[] {
  if (view.narrow) return [s.panes[s.activePane].tabIds.length ? s.activePane : 'left'];
  return s.paneCount === 2 && view.wide ? ['left', 'right'] : ['left'];
}

/** De gesprekken in beeld: dat van het actieve chattabblad van elk paneel in beeld. */
export function visibleChats(s: TabsState, view: { narrow: boolean; wide: boolean }): Set<string> {
  const ids = new Set<string>();
  for (const p of visiblePanes(s, view)) {
    const id = s.panes[p].activeTabId;
    const t = id ? s.tabs[id] : undefined;
    const chatId = t?.kind === 'chat' ? t.state['chatId'] : undefined;
    if (typeof chatId === 'string') ids.add(chatId);
  }
  return ids;
}

/** Heeft dit gesprek een antwoord dat de eigenaar nog niet zag? */
export function isUnread(s: Pick<TabsState, 'unread'>, chatId: string): boolean {
  return (s.unread ?? []).includes(chatId);
}

/** Zolang na het einde van een beurt zonder antwoord in de store telt een antwoord dat dan nog binnenkomt (zie de kop). */
export const LATE_MS = 120_000;

/** Waar een beurt begon: het hoogste volgnummer dat de store toen van het gesprek kende (null als ze er geen kende), en de laatste
 * wijziging van het gesprek op de server (de klok van de server, net als `createdAt` van de berichten). */
interface TurnMark {
  seq: number | null;
  at: number;
}

/** Schreef Jerome sinds `mark` een antwoord: tekst, of een fout als laatste bericht (zie de kop)? Nieuw is een bericht met een hoger
 * volgnummer dan `mark`, of zonder bekend volgnummer een bericht van na de laatste wijziging op de server. */
function answeredSince(list: readonly Message[], mark: TurnMark): boolean {
  const fresh = (m: Message) => (mark.seq !== null ? m.seq > mark.seq : Date.parse(m.createdAt) >= mark.at);
  const said = (m: Message) => String(m.content['text'] ?? '').trim() !== '';
  if (list.some((m) => m.role === 'assistant' && m.kind === 'text' && said(m) && fresh(m))) return true;
  const last = list.reduce<Message | undefined>((a, m) => (!a || m.seq > a.seq ? m : a), undefined);
  return Boolean(last && last.role === 'assistant' && last.kind === 'error' && fresh(last));
}

/**
 * Bewaakt de beurten van Jerome (zie de kop van dit bestand): legt het begin van elke beurt vast in `turnStarts`, zet een gesprek met
 * een nieuw antwoord buiten beeld in `unread` en wist de stip zodra het gesprek in beeld is. De schil start haar; geeft de opruimer.
 * `doc`: het document met de zichtbaarheid van het venster (in tests een nagebootst document).
 */
export function watchChatTurns(doc: Document = document): () => void {
  const marks = new Map<string, TurnMark>();
  /** Beurten die eindigden zonder antwoord in de store, met tot wanneer een antwoord nog meetelt (zie de kop). */
  const late = new Map<string, TurnMark & { until: number }>();
  let prev = chatStore.get().chats;
  let prevMessages = chatStore.get().messages;
  let loadedBefore = chatStore.get().loaded;

  const shown = () => doc.visibilityState === 'visible';
  const inView = (chatId: string) => shown() && visibleChats(tabs.get(), app.get()).has(chatId);
  const dropStart = (chatId: string) =>
    turnStarts.set((s) => {
      if (!(chatId in s)) return s;
      const next = { ...s };
      delete next[chatId];
      return next;
    });

  /** Een beurt begint; `live`: de client zag het gesprek op bezig gaan (anders was het al bezig toen hij het voor het eerst zag). */
  const begin = (chat: Chat, live: boolean) => {
    late.delete(chat.id);
    stopRequests.delete(chat.id);
    const list = chatStore.get().messages[chat.id] ?? [];
    marks.set(chat.id, { seq: list.length ? Math.max(...list.map((m) => m.seq)) : null, at: Date.parse(chat.updatedAt) || 0 });
    // Een begin dat al vastlag (de schil begon de bewaking opnieuw), blijft; een beurt die de client zag beginnen, begint nu.
    if (!live && turnStarts.get()[chat.id] !== undefined) return;
    const at = live ? Date.now() : turnStart(chat, {});
    turnStarts.set((s) => ({ ...s, [chat.id]: at }));
  };

  const markUnread = (chatId: string) => {
    if (inView(chatId)) return;
    tabs.set((s) => {
      const list = s.unread ?? [];
      return list.includes(chatId) ? s : { ...s, unread: [...list, chatId].slice(-UNREAD_KEPT) };
    });
  };

  const end = (chat: Chat) => {
    const mark = marks.get(chat.id);
    marks.delete(chat.id);
    dropStart(chat.id);
    const stopped = stopRequests.delete(chat.id);
    if (chat.kind !== 'chat' || !mark || stopped) return;
    if (answeredSince(chatStore.get().messages[chat.id] ?? [], mark)) markUnread(chat.id);
    else late.set(chat.id, { ...mark, until: Date.now() + LATE_MS });
  };

  /** Een antwoord dat na de rust binnenkwam, met de berichten (zie de kop). */
  const checkLate = () => {
    const now = Date.now();
    for (const [chatId, mark] of late) {
      if (now > mark.until) late.delete(chatId);
      else if (answeredSince(chatStore.get().messages[chatId] ?? [], mark)) {
        late.delete(chatId);
        markUnread(chatId);
      }
    }
  };

  /** Wist de stip van elk gesprek dat nu in beeld is terwijl het venster zichtbaar is. */
  const clearSeen = () => {
    if (!shown()) return;
    const list = tabs.get().unread;
    if (!list?.length) return;
    const seen = visibleChats(tabs.get(), app.get());
    if (!list.some((id) => seen.has(id))) return;
    tabs.set((s) => ({ ...s, unread: (s.unread ?? []).filter((id) => !seen.has(id)) }));
  };

  const onChats = () => {
    const s = chatStore.get();
    if (s.chats !== prev) {
      for (const c of Object.values(s.chats)) {
        const was = prev[c.id];
        if (c.status === 'running' && was?.status !== 'running') begin(c, Boolean(was) || loadedBefore);
        else if (c.status !== 'running' && was?.status === 'running') end(c);
      }
      // Verwijderd: de beurt telt niet meer.
      for (const id of Object.keys(prev))
        if (!s.chats[id]) {
          late.delete(id);
          if (marks.delete(id)) dropStart(id);
        }
      prev = s.chats;
    }
    if (s.messages !== prevMessages) {
      prevMessages = s.messages;
      if (late.size) checkLate();
    }
    loadedBefore = s.loaded;
  };

  // Wat al bezig was toen de bewaking begon; een vastgelegd begin van een gesprek dat niet meer bezig is, gaat weg.
  for (const c of Object.values(prev)) if (c.status === 'running') begin(c, false);
  turnStarts.set((s) => {
    const stale = Object.keys(s).filter((id) => prev[id]?.status !== 'running');
    if (!stale.length) return s;
    const next = { ...s };
    for (const id of stale) delete next[id];
    return next;
  });

  const offChats = chatStore.subscribe(onChats);
  const offTabs = tabs.subscribe(clearSeen);
  const offApp = app.subscribe(clearSeen);
  doc.addEventListener('visibilitychange', clearSeen);
  clearSeen();
  return () => {
    offChats();
    offTabs();
    offApp();
    doc.removeEventListener('visibilitychange', clearSeen);
  };
}
